import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Trash2, Zap, Send, Hash, MapPin } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Location {
  id: string;
  name: string;
}

interface TableMessage {
  id: string;
  location_id: string;
  sender_table_id: string;
  receiver_table_id: string;
  message: string;
  is_challenge: boolean;
  created_at: string;
  location_name: string;
  sender_table_number: number;
  receiver_table_number: number;
}

const Messaggi: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [messages, setMessages] = useState<TableMessage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadLocations();
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('table_messages')
        .select(`
          id,
          location_id,
          sender_table_id,
          receiver_table_id,
          message,
          is_challenge,
          created_at,
          locations!inner(name),
          sender:tavoli!table_messages_sender_table_id_fkey(table_number),
          receiver:tavoli!table_messages_receiver_table_id_fkey(table_number)
        `)
        .order('created_at', { ascending: false })
        .limit(200);

      if (selectedLocationId) {
        query = query.eq('location_id', selectedLocationId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const mapped: TableMessage[] = (data || []).map((row: any) => ({
        id: row.id,
        location_id: row.location_id,
        sender_table_id: row.sender_table_id,
        receiver_table_id: row.receiver_table_id,
        message: row.message,
        is_challenge: row.is_challenge,
        created_at: row.created_at,
        location_name: row.locations?.name || '',
        sender_table_number: row.sender?.table_number || 0,
        receiver_table_number: row.receiver?.table_number || 0,
      }));

      setMessages(mapped);
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const loadLocations = async () => {
    const { data } = await supabase
      .from('locations')
      .select('id, name')
      .eq('status', 'active')
      .order('name');
    setLocations(data || []);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Eliminare questo messaggio?')) return;
    try {
      const { error } = await supabase
        .from('table_messages')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalMessages = messages.length;
  const challengeMessages = messages.filter(m => m.is_challenge).length;
  const normalMessages = totalMessages - challengeMessages;
  const uniqueLocations = new Set(messages.map(m => m.location_id)).size;

  return (
    <div className="p-8">
      {/* Stats banner */}
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Totale Messaggi</p>
                  <p className="text-3xl font-bold text-white mt-1">{totalMessages}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <MessageCircle className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Messaggi</p>
                  <p className="text-3xl font-bold text-white mt-1">{normalMessages}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Send className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Sfide</p>
                  <p className="text-3xl font-bold text-white mt-1">{challengeMessages}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Zap className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Locali Attivi</p>
                  <p className="text-3xl font-bold text-white mt-1">{uniqueLocations}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header with filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestione Messaggi</h1>
        <select
          value={selectedLocationId}
          onChange={(e) => setSelectedLocationId(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent"
        >
          <option value="">Tutti i locali</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>{loc.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Locale</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Da</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">A</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Messaggio</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Stato</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Data</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400 text-sm">
                    Caricamento...
                  </td>
                </tr>
              ) : messages.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400 text-sm">
                    Nessun messaggio presente
                  </td>
                </tr>
              ) : (
                messages.map((msg) => (
                  <tr key={msg.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm text-gray-700">{msg.location_name}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">
                      Tavolo {msg.sender_table_number}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">
                      Tavolo {msg.receiver_table_number}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700 max-w-xs truncate">
                      {msg.message}
                    </td>
                    <td className="py-3 px-4">
                      {msg.is_challenge ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <Zap className="h-3 w-3" /> Sfida
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          Messaggio
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(msg.created_at)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => handleDelete(msg.id)}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Elimina messaggio"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Messaggi;
